/**
 * markdown-lite.tsx — 聊天区轻量 markdown 渲染器(零依赖)
 *
 * 背景(r2 契约 item 4):/coach 聊天区裸露 `**`(r1 实测 52 处)/`## `(3 处)——
 * 教练回复来自 LLM,天然带 markdown,聊天气泡此前按纯文本渲染,学习者可见原文记号。
 * 方案:不引新依赖(package.json 无 markdown 库),自写小渲染器,覆盖 LLM 中文教学
 * 回复实际出现的语法:# / ## / ### 标题、**加粗**、`行内代码`、- / * 列表、
 * 1. 有序列表、> 引用、--- 分隔线。
 *
 * 契约保证(probe/learner-dom-surface/ 屏级机检):
 *   - 行首 `#{1,6} ` 一律被标题消费 → innerText 无 `^## `;
 *   - `**` 成对被加粗消费;未闭合 `**`(如流式截断)静默丢弃 → innerText 无 `**`。
 *
 * 刻意不处理:*斜体*(数字/运算符场景易误伤,判据也不要求)、表格/链接/图片
 * (教学回复不出现)。plain 文本逐字透传,不改变任何无 markdown 内容的呈现。
 */
import { useMemo, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type MdBlock =
  | { kind: "heading"; level: number; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: { n: string; text: string }[] }
  | { kind: "quote"; text: string }
  | { kind: "hr" };

const RE_HEADING = /^(#{1,6})\s+(.+)$/;
const RE_HR = /^(-{3,}|\*{3,}|_{3,})$/;
const RE_BULLET = /^[-*]\s+(.+)$/;
const RE_NUMBERED = /^(\d+)[.、)]\s+(.+)$/;
const RE_QUOTE = /^>\s?(.*)$/;

/** 逐行分组为块;空行分段。导出供单测。 */
export function parseBlocks(text: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  let para: string[] = [];
  let ul: string[] | null = null;
  let ol: { n: string; text: string }[] | null = null;

  const flushPara = () => {
    if (para.length > 0) {
      blocks.push({ kind: "p", text: para.join("\n") });
      para = [];
    }
  };
  const flushLists = () => {
    if (ul) {
      blocks.push({ kind: "ul", items: ul });
      ul = null;
    }
    if (ol) {
      blocks.push({ kind: "ol", items: ol });
      ol = null;
    }
  };

  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (line === "") {
      flushPara();
      flushLists();
      continue;
    }
    const h = RE_HEADING.exec(line);
    if (h) {
      flushPara();
      flushLists();
      blocks.push({ kind: "heading", level: h[1].length, text: h[2] });
      continue;
    }
    if (RE_HR.test(line)) {
      flushPara();
      flushLists();
      blocks.push({ kind: "hr" });
      continue;
    }
    const bu = RE_BULLET.exec(line);
    if (bu) {
      flushPara();
      if (ol) flushLists();
      if (!ul) ul = [];
      ul.push(bu[1]);
      continue;
    }
    const nu = RE_NUMBERED.exec(line);
    if (nu) {
      flushPara();
      if (ul) flushLists();
      if (!ol) ol = [];
      ol.push({ n: nu[1], text: nu[2] });
      continue;
    }
    const q = RE_QUOTE.exec(line);
    if (q) {
      flushPara();
      flushLists();
      blocks.push({ kind: "quote", text: q[1] });
      continue;
    }
    flushLists();
    para.push(line);
  }
  flushPara();
  flushLists();
  return blocks;
}

/**
 * 行内渲染:`**bold**` → <strong>、`code` → <code>;
 * 未闭合的 `**`(截断残留)被第三个分支吞掉、零输出——innerText 永不出现 `**`。
 * 导出供单测。
 */
export function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|`([^`]+)`|\*\*/g;
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      nodes.push(
        <strong key={k++} className="font-semibold text-amber-200">
          {m[1]}
        </strong>,
      );
    } else if (m[2] !== undefined) {
      nodes.push(
        <code
          key={k++}
          className="rounded bg-[#1E2E23] px-1 py-px font-mono text-[0.85em] text-emerald-200"
        >
          {m[2]}
        </code>,
      );
    }
    // m[0] === "**"(未闭合)→ 不输出任何内容
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** 聊天气泡内的 markdown 渲染入口:继承外层字号/行高,块间留小间距。 */
export function MarkdownLite({ text, className }: { text: string; className?: string }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return (
    <div className={cn("space-y-1.5", className)}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "heading":
            return (
              <p key={i} className={cn("font-bold text-stone-50", i > 0 && "pt-1")}>
                {renderInline(b.text)}
              </p>
            );
          case "p":
            return (
              <p key={i} className="whitespace-pre-wrap">
                {renderInline(b.text)}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="space-y-0.5">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-1.5">
                    <span aria-hidden className="mt-px shrink-0 text-emerald-400/70">
                      •
                    </span>
                    <span>{renderInline(it)}</span>
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="space-y-0.5">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-1.5">
                    <span aria-hidden className="shrink-0 tabular-nums text-stone-500">
                      {it.n}.
                    </span>
                    <span>{renderInline(it.text)}</span>
                  </li>
                ))}
              </ol>
            );
          case "quote":
            return (
              <p key={i} className="border-l-2 border-emerald-400/40 pl-2 text-stone-300">
                {renderInline(b.text)}
              </p>
            );
          case "hr":
            return <div key={i} aria-hidden className="h-px bg-[#1E2E23]" />;
        }
      })}
    </div>
  );
}
